import React from "react";
import { createReactMount } from "@/main/react/mountContract";
import { ClientFactory } from "@/main/common/ClientFactory";
import { GroupDTO, GroupTagCountDTO } from "@/generated-client";
import { MetaHandler } from "@/main/common/MetaHandler";
import { Localizer } from "@/main/react/Types";
import { PageWithRailLayout } from "@/main/react/page/PageWithRailLayout";
import { useHistoryBackedState } from "@/main/react/page/useHistoryBackedState";
import { useTypedTextHistorySession } from "@/main/react/page/useTypedTextHistorySession";
import { GroupCardDisplayer } from "@/main/react/card/GroupCardDisplayer";
import { CtaButton } from "@/main/react/buttons/CtaButton";
import { WarningBanner } from "@/main/react/component/WarningBanner";
import { CRAWLABLE_GROUP_LIST_SSR_SELECTOR, SsrRestoredList } from "@/main/react/list/ssrFallbacks";
import { showCreateGroupDialog, showUpdateGroupSettingsDialog } from "@/main/react/Dialog/UpdateGroupSettingsDialog";
import { GroupLanguageScope, GroupLanguageScopeSelect } from "@/main/react/page/groups-directory/GroupLanguageScopeSelect";
import SearchBlendDivider from "@/main/react/page/search/SearchBlendDivider";
import { ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface } from "@/main/react/account_access/AccountAccessDialogContext";
import { PaginatedListStatusText } from "@/main/react/list/PaginatedListState";
import { ArrowUp, Check, ChevronDown, Compass, Hash, Languages, PlusCircle, Search, Sparkles, X } from "lucide-react";
import { cn } from "@/lib/utils";
import logger from "@/lib/logger";
import { replaceInPageHistoryUrl } from "@/main/navigation/NavigationHistoryState";
import { useFloatingActionGroup } from "@/main/react/floating-actions/FloatingActionContributionStore";
import { useIsMobileViewport } from "@/main/react/hooks/useIsMobileViewport";
import { prefersReducedMotion } from "@/main/util/motionPreference";

const SEARCH_DEBOUNCE_MS = 300;
const POPULAR_TAGS_LIMIT = 16;
const MOBILE_TOPIC_CHIPS_LIMIT = 10;

/**
 * How many "related to groups you're in" cards the zero-match branch shows (`EMPTY-STATES-94`).
 * The same cap the browse row uses, so a member meets the row at one size wherever it appears.
 */
const ZERO_MATCH_RELATED_LIMIT = 4;

/**
 * Below this many rows a settled, unpaginated list gets NO end-of-results marker.
 *
 * `EMPTY-STATES-52` wants the marker because a twenty-card page that simply stops is ambiguous;
 * `DASHBOARD-32` refused it because "End of list" under a single card is pagination machinery leaking
 * into user-facing copy (rule 12) and reads as if the list had been cut short. The rule that satisfies
 * both is about **pagination having happened**, not about length — so the marker also renders
 * unconditionally once the viewer has actually loaded a further page (see `hasPaginated`). Eight is
 * four rows of the two-column grid, i.e. the point at which the eye can no longer see the whole list
 * at once and "did it stop or did it fail?" becomes a real question.
 */
const END_OF_RESULTS_MIN_ITEMS = 8;
/**
 * The directory's search names its own surface (BL-169). `"directory"` is the exact member of
 * `AjaxPaginationController.CALLER_SUPPLIED_SEARCH_SURFACES`; any other value records `page`.
 */
const DIRECTORY_SEARCH_OPTIONS = { params: { surface: "directory" } } as const;

const CREATE_GROUP_ACCOUNT_ACCESS_DIALOG = withAccountAccessSurface(
    ACCOUNT_ACCESS_DIALOG_COPY.createSpace,
    "groups_directory_create",
);

/**
 * The three controls in this page's header row, and all three round-trip through the URL.
 *
 * `scope` joined `query` and `tag` here for `EMPTY-STATES-29`: it sat in a bare `useState` while the
 * two controls beside it were URL-backed, so widening to "All languages" and then reloading, sharing
 * the link or pressing Back silently snapped the page back to the viewer's own languages while the
 * query and tag survived — two controls in one row behaving in opposite ways with nothing on screen
 * marking the difference. A shared link now carries the sender's scope; `mine` is the default and is
 * omitted from the URL, so the ordinary address does not grow.
 *
 * Rule 33 holds by construction: `useHistoryBackedState` is the sanctioned wrapper over
 * `NavigationHistoryState`, so nothing here calls `history.pushState`/`replaceState` and no entry is
 * built by spreading the current one.
 */
type GroupsDirectoryRouteState = {
    query: string;
    tag: string;
    scope: GroupLanguageScope;
};

/**
 * One page of directory rows plus, for a SEARCH, how many of them actually matched the query.
 *
 * `strongMatches` is `null` on browse and tag modes (neither blends, so every row is a real row) and
 * also whenever the server did not say — an older backend or a proxy that dropped the header. `null`
 * must render no seam at all: treating it as zero would manufacture the very claim the count exists to
 * make honest. See `ClientFactory.readStrongMatchCount`.
 */
type GroupsDirectoryPageResult = {
    items: GroupDTO[];
    strongMatches: number | null;
    /**
     * For BROWSE, how many groups the applied language scope holds in total — the honest total behind
     * a page of 24 (`DISCOVERY-12`). `null` on search and tag modes, on a continuation page, and
     * whenever the server did not say. Same rule as `strongMatches`: `null` renders nothing, never
     * zero. See `ClientFactory.readTotalCount`.
     */
    totalCount: number | null;
};

export type GroupsDirectoryPageProps = {
    initialQuery?: string;
    initialTag?: string;
    localizer: Localizer;
};

function readStateFromSearch(search: string): GroupsDirectoryRouteState {
    try {
        const params = new URLSearchParams(search);
        return {
            query: params.get("q")?.trim() ?? "",
            tag: params.get("tag")?.trim() ?? "",
            // Anything that is not the explicit widening resolves to the recorded default. See
            // designs/01-now/2026-07-29-your-languages-reading-set-scope-design.md — "mine" is a
            // decision, not an accident, and this row only makes it legible.
            scope: params.get("scope")?.trim() === "all" ? "all" : "mine",
        };
    } catch {
        return { query: "", tag: "", scope: "mine" };
    }
}

function getGroupKey(group: GroupDTO, index: number): string {
    return group.publicId ?? group.slug ?? group.name ?? `group-${index}`;
}

function GroupsGrid({
    groups,
    localizer,
    playwrightId = "groups-directory-grid",
}: { groups: GroupDTO[]; localizer: Localizer; playwrightId?: string }) {
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-playwright-id={playwrightId}>
            {groups.map((group, index) => (
                <GroupCardDisplayer key={getGroupKey(group, index)} group={group} surface="groups_directory" localizer={localizer} />
            ))}
        </div>
    );
}

function GroupsGridSkeleton() {
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" aria-hidden={true} data-playwright-id="groups-directory-skeleton">
            {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="h-40 animate-pulse rounded-2xl border border-border bg-muted" />
            ))}
        </div>
    );
}

/**
 * `EMPTY-STATES-39`, heading half. The title is an `<h2>`, not a `<div>`.
 *
 * <p>`groups_directory.jte` gives the page one `<h1>` and this panel replaces the entire results
 * region, so with the title as a `<div>` a screen-reader user who reached a directory with no results
 * had nothing between the page heading and the footer: no announced boundary, and no way to find what
 * the region even was by heading navigation. Level 2 because it labels a top-level region of the page
 * and the page's `<h1>` is its own.
 *
 * <p>Tailwind preflight strips the browser's default heading size and weight, and the explicit
 * `text-base font-semibold` below was already carrying both, so the rendering is byte-identical. This
 * is an element change, not a styling one — the same shape as the fix applied to the group page's
 * section panels and the dashboard's.
 */
function EmptyState({ title, description, action }: { title: string; description: string; action?: React.ReactNode }) {
    return (
        <div className="rounded-2xl border border-dashed border-border bg-muted px-4 py-10 text-center">
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-card text-text-main">
                <Compass className="size-5" aria-hidden={true} />
            </div>
            <h2 className="mt-4 text-base font-semibold text-text-main">{title}</h2>
            <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-neutral-text">{description}</p>
            {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
        </div>
    );
}

/**
 * A topic filter chip.
 *
 * `DISCOVERY-24`: `active` used to feed `cn()` and nothing else, so the selected chip differed from
 * its fifteen neighbours by a tint and by nothing at all in the accessibility tree. It is a toggle,
 * not navigation — the chip does not take you anywhere, it narrows the list you are already reading —
 * so the pressed state is `aria-pressed`, and the selected chip now swaps its leading glyph from the
 * hash to a check, which is a second, non-colour channel for the same fact.
 */
/**
 * `DISCOVERY-48` — pill weight tracks interactivity, not subject matter.
 *
 * The topic filter chips and a group card's metadata badges were the same object visually: both
 * `rounded-full`, bordered, `text-xs`, with a small leading lucide glyph at near-identical padding,
 * both painting their label in full foreground on their own surface. The two things that separated
 * them — a hover response and `aria-pressed` — are both invisible to a touch user, and on a phone the
 * filter strip and the first card's badges sit roughly 200 CSS px apart in one scroll column. So the
 * one control that narrows a directory of unknown size read as more decoration on a card.
 *
 * <p>This is the recipe `DISCOVERY-30` settled on `/search` and `QuestionsFilterChip` carries: a
 * filled RESTING surface a caption does not get, the single hover wash (design-language rule 4, and
 * not the two-property border+fill response this used to make), and the one shared focus ring
 * (rule 5) rather than a bespoke outline. The shape stays a pill — these genuinely are one visual
 * family, which is why the fix is weight rather than geometry.
 *
 * <p>The other direction the row offers — letting the card badges recede — is NOT available:
 * `CardMetadataBadges`' resting recipe is pinned by an operator decision
 * (`designs/01-now/2026-08-11-header-count-chips-as-navigation-design.md` §3.1, "leave the RESTING
 * recipe alone"), it is shared byte-for-byte with the interactive count chips on purpose, and
 * changing it would restyle every caption badge in the product.
 */
function TagChip({
    label,
    count,
    active,
    onClick,
    className,
}: { label: string; count?: number | null; active?: boolean; onClick: () => void; className?: string }) {
    const Glyph = active ? Check : Hash;
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={Boolean(active)}
            className={cn(
                // `DISCOVERY-67` — the floor every CTA beside these chips already takes. In the frame
                // that filed the row both card CTAs measured 44.2 CSS px (which is what proves
                // `(pointer: coarse)` was active rather than the usual headless artifact) while this
                // chip measured 30.1: `px-3 py-1.5 text-xs` plus a 1 px border. On a phone these chips
                // ARE the browse-by-topic affordance — the rail carrying the topic list is `lg:` only —
                // and a miss lands on a neighbouring chip, which REPLACES the query rather than
                // narrowing it, so a fat-finger costs the words the visitor typed (`DISCOVERY-60`).
                //
                // `min-h-11` rather than a fixed `h-11`: a floor survives flex-shrink and a size does
                // not, which is check A of `check-tap-target-mechanisms.mjs`. Gated on the INPUT DEVICE
                // and never on a breakpoint (check C). Horizontal pitch already clears 44 — the
                // narrowest chip is 88 CSS px plus `gap-2` — so only the vertical axis was owed, and the
                // scroller's own height grows with it at the call site below, because `overflow-x: auto`
                // clips anything taller than the row.
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors [@media(pointer:coarse)]:min-h-11",
                "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                active
                    ? "border-primary/40 bg-primary/10 text-primary"
                    : "border-border bg-background2/60 text-foreground hover:bg-accent",
                className,
            )}
            data-playwright-id="groups-directory-tag-chip"
            data-active={active ? "true" : "false"}
        >
            <Glyph className="size-3" aria-hidden={true} />
            <span className="truncate">{label}</span>
            {count != null ? <span className="text-neutral-text">{count}</span> : null}
        </button>
    );
}

function DirectoryRail({
    localizer,
    popularTags,
    activeTag,
    onSelectTag,
    seedName,
}: {
    localizer: Localizer;
    popularTags: GroupTagCountDTO[];
    activeTag: string;
    onSelectTag: (tag: string) => void;
    /**
     * The search string to pre-fill the new group's name with, or `""` for a blank form
     * (`EMPTY-STATES-91`).
     *
     * <p>This button and the zero-match branch's create button are pixel-identical — same icon, same
     * label, same variant, same measured fill — and until now they did different things: the
     * zero-match one seeded the name with what the member typed, this one opened a blank form. Half
     * the members who match nothing reach for this one, because it is wider, it sits in a box headed
     * "Start your own group", and on desktop it is higher on the page. They got exactly the blank
     * form the seeding fix was written to eliminate.
     *
     * <p>Both carrying the seed is the cheapest correct shape: the choice stops being a coin flip
     * without either control having to change how it looks or where it sits.
     */
    seedName: string;
}) {
    return (
        <aside className="flex min-w-0 w-full flex-col gap-4 lg:sticky lg:top-4 lg:self-start">
            <section className="min-w-0 rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-card text-foreground">
                        <PlusCircle className="size-4.5" aria-hidden={true} />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-text-main">{localizer("groups.directory.create.title")}</div>
                        <p className="mt-1 text-sm leading-6 text-neutral-text">{localizer("groups.directory.create.description")}</p>
                    </div>
                </div>
                <div className="mt-4">
                    <CtaButton
                        icon={PlusCircle}
                        text={localizer("groups.directory.create.button")}
                        displayTextOnMobile={true}
                        variant="primary"
                        extraClass="w-full justify-center"
                        requiresLogin
                        accountAccessDialog={CREATE_GROUP_ACCOUNT_ACCESS_DIALOG}
                        playwrightId="groups-directory-create"
                        onClick={() => {
                            // `showUpdateGroupSettingsDialog` with no `groupPublicId` IS a creation —
                            // the same call the zero-match branch makes — so carrying the seed here
                            // is an argument, not a second code path.
                            if (seedName) {
                                showUpdateGroupSettingsDialog({ localizer, name: seedName });
                            } else {
                                showCreateGroupDialog(localizer);
                            }
                        }}
                    />
                </div>
            </section>

            {/* `hidden lg:block`: below `lg` the rail itself now renders (see `railOnMobile` on the
                layout below, and `DISCOVERY-11`), while the topic list already has a mobile home at
                the top of the main column. Without this the same sixteen chips would be painted twice
                on one phone screen. On mobile the rail contributes the create card and nothing else,
                which is the affordance that had no mobile home at all. */}
            {popularTags.length > 0 ? (
                <section
                    className="hidden min-w-0 rounded-2xl border border-border bg-card p-4 lg:block"
                    data-playwright-id="groups-directory-rail-topics"
                >
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-main">
                        {/* `text-neutral` KEPT here and on the Sparkles ornament below, deliberately, in
                            the 2026-08-16 `text-neutral` → `text-neutral-text` sweep (`DISCOVERY-1`).
                            `--neutral` is gray-400 in light and measures ~2.4:1, so it may not carry
                            TEXT — but these two are `aria-hidden` ornaments sitting beside their own
                            written label, conveying nothing the label does not, which is the fill role
                            the token exists for. Every glyph in this file that a reader has to FIND
                            rather than merely notice (the field's magnifier, the clear-search control)
                            was swapped. */}
                        <Hash className="size-4 text-neutral" aria-hidden={true} />
                        {localizer("groups.directory.topics.title")}
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {activeTag ? (
                            <TagChip label={localizer("groups.directory.topics.all")} active={false} onClick={() => onSelectTag("")} />
                        ) : null}
                        {popularTags.map((tag) => (
                            <TagChip
                                key={tag.name ?? ""}
                                label={tag.name ?? ""}
                                count={tag.groupCount}
                                active={Boolean(tag.name) && tag.name === activeTag}
                                onClick={() => onSelectTag(tag.name ?? "")}
                            />
                        ))}
                    </div>
                </section>
            ) : null}
        </aside>
    );
}

export function GroupsDirectoryPage({ initialQuery = "", initialTag = "", localizer }: GroupsDirectoryPageProps) {
    const paginationApi = React.useMemo(() => ClientFactory.getAjaxPaginationRestControllerApi(), []);
    const groupApi = React.useMemo(() => ClientFactory.getAjaxGroupRestControllerApi(), []);
    const isLoggedIn = MetaHandler.isUserLoggedIn();

    const { state: routeState, setState: setRouteState } = useHistoryBackedState<GroupsDirectoryRouteState>({
        initialState: { query: initialQuery.trim(), tag: initialTag.trim(), scope: "mine" },
        readState: (location, fallback) => {
            const parsed = readStateFromSearch(location.search);
            // `scope` is taken from the URL outright rather than falling back: unlike `q` and `tag`,
            // which the server also seeds from the request path, the scope has exactly one source and
            // its absence is a meaningful value ("mine", the recorded default).
            return { query: parsed.query || fallback.query, tag: parsed.tag || fallback.tag, scope: parsed.scope };
        },
        buildUrl: (state, location) => {
            const params = new URLSearchParams(location.search);
            if (state.query.trim()) {
                params.set("q", state.query.trim());
            } else {
                params.delete("q");
            }
            if (state.tag.trim()) {
                params.set("tag", state.tag.trim());
            } else {
                params.delete("tag");
            }
            if (state.scope === "all") {
                params.set("scope", "all");
            } else {
                params.delete("scope");
            }
            const queryString = params.toString();
            return `/groups${queryString ? `?${queryString}` : ""}${location.hash}`;
        },
        areEqual: (left, right) => left.query === right.query && left.tag === right.tag && left.scope === right.scope,
    });

    const query = routeState.query.trim();
    const tag = routeState.tag.trim();
    const languageScope = routeState.scope;
    const mode: "search" | "tag" | "browse" = query.length > 0 ? "search" : (tag.length > 0 ? "tag" : "browse");

    /**
     * One typed-text editing session owns one history entry, shared with the search box below (§13.4).
     * Every control on this page that is NOT the box ends the session, so the next keystroke opens a
     * fresh entry rather than extending one nobody is editing.
     */
    const { optionsForTypedValue, endTypingSession } = useTypedTextHistorySession();

    // Page-level "All groups / Your languages" scope, applied to whichever mode is active. Defaults
    // to the viewer's reading languages (mirrors the profile discussion toggle, inverted default) and
    // round-trips through the URL beside `q` and `tag` (`EMPTY-STATES-29`). PUSHES: it changes the
    // address, so Back returns to the previous scope (§13.1).
    const setLanguageScope = React.useCallback((nextScope: GroupLanguageScope) => {
        endTypingSession();
        setRouteState((previous) => ({ ...previous, scope: nextScope }));
    }, [endTypingSession, setRouteState]);

    const [inputValue, setInputValue] = React.useState(query);
    const [groups, setGroups] = React.useState<GroupDTO[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [hasMore, setHasMore] = React.useState(false);
    // Whether the viewer has ever loaded a further page in this session. The end-of-results marker is
    // about pagination having happened, not about list length — see END_OF_RESULTS_MIN_ITEMS.
    const [hasPaginated, setHasPaginated] = React.useState(false);
    // How many of `groups` actually matched the query, for a search. `null` on browse/tag and whenever
    // the server did not report it. See `GroupsDirectoryPageResult`.
    const [strongMatchCount, setStrongMatchCount] = React.useState<number | null>(null);
    /**
     * How many groups the applied language scope holds in total — `DISCOVERY-12`.
     *
     * `null` on search and tag modes and whenever the server did not say, and `null` renders nothing.
     * A missing header must never read as zero: that would turn "we could not count" into "the
     * catalogue is empty", which is the strongest possible version of the claim the count exists to
     * make honest.
     */
    const [totalCount, setTotalCount] = React.useState<number | null>(null);
    const requestIdRef = React.useRef(0);

    const [popularTags, setPopularTags] = React.useState<GroupTagCountDTO[]>([]);
    // False until the popular-tags request has answered, either way. The mobile chip strip reserves
    // its height while this is false, so the chips arriving cannot shove the results down
    // (`DISCOVERY-13`).
    const [popularTagsLoaded, setPopularTagsLoaded] = React.useState(false);
    const [relatedToMine, setRelatedToMine] = React.useState<GroupDTO[]>([]);
    // The mobile topic strip's written caption, which is also the scroller's accessible name
    // (`EMPTY-STATES-92`). Generated rather than a constant: the component gallery mounts this page
    // beside others, and two elements sharing one id would silently hand the wrong name to the second.
    const topicsCaptionId = React.useId();

    /*
     * `DISCOVERY-55` — the mobile topic strip's overflow affordance, the one its sibling on `/search`
     * has owned since `DISCOVERY-4`.
     *
     * At 412 px the strip is a single-row `overflow-x-auto` scroller with no fade, no scrollbar, no
     * snap and no arrow, and it does NOT bleed to the viewport edge: the chip fill stops at 380 CSS px
     * with 32 px of page background beyond it, so a clipped chip reads as content overflowing its
     * column rather than as a row that continues off-screen. On a phone this strip is the only
     * browse-by-topic affordance there is (the rail carrying the topic list is `lg:` only) and
     * `MOBILE_TOPIC_CHIPS_LIMIT` is 10, so in a populated environment roughly seven topics sit behind
     * a gesture nobody has reason to suspect.
     *
     * Same mechanism as `SearchTabsNav`, deliberately, so one vocabulary covers both discovery strips:
     * each side renders only while THAT side is actually clipped, the overlays are `aria-hidden` and
     * `pointer-events-none` (a visual cue that the row continues, never content and never a control —
     * every chip is already in the tab order), and the 1 px slack absorbs sub-pixel layout.
     *
     * It costs no height, which `DISCOVERY-13`'s reservation requires: the overlays are absolutely
     * positioned against a `relative` wrapper that adds no box of its own.
     */
    const topicScrollerRef = React.useRef<HTMLDivElement | null>(null);
    const [topicScrollerClippedEdges, setTopicScrollerClippedEdges] = React.useState({
        start: false,
        end: false,
    });
    const syncTopicScrollerClippedEdges = React.useCallback(() => {
        const scroller = topicScrollerRef.current;
        if (!scroller) {
            return;
        }
        const start = scroller.scrollLeft > 1;
        const end = scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1;
        setTopicScrollerClippedEdges((previous) => (
            previous.start === start && previous.end === end ? previous : { start, end }
        ));
    }, []);

    React.useEffect(() => {
        const scroller = topicScrollerRef.current;
        if (!scroller || typeof ResizeObserver === "undefined") {
            return;
        }
        const observer = new ResizeObserver(() => syncTopicScrollerClippedEdges());
        observer.observe(scroller);
        return () => observer.disconnect();
    }, [syncTopicScrollerClippedEdges]);

    // The scroller's own box never changes size when the chips arrive — it is full width at a fixed
    // height — so the ResizeObserver above cannot see the one event that decides whether the row is
    // clipped. Re-measured on the CONTENT instead: the tags landing, and the "All topics" chip
    // appearing or leaving with `tag`.
    React.useEffect(() => {
        syncTopicScrollerClippedEdges();
    }, [popularTags, popularTagsLoaded, tag, syncTopicScrollerClippedEdges]);

    React.useEffect(() => {
        setInputValue(query);
    }, [query]);

    // `/groups?create=1` is the landing-page hand-off: the community-family CTAs ("Create your
    // community/forum/group") send an already-logged-in visitor straight here, so the create dialog
    // must open on arrival — otherwise the button promises creation and delivers a browse page.
    // Fires once, then strips the param so a refresh or back-navigation does not re-open the dialog.
    const createDialogOpenedRef = React.useRef(false);
    React.useEffect(() => {
        if (createDialogOpenedRef.current || !isLoggedIn) {
            return;
        }
        let shouldOpen = false;
        try {
            shouldOpen = new URLSearchParams(window.location.search).get("create") === "1";
        } catch {
            shouldOpen = false;
        }
        if (!shouldOpen) {
            return;
        }
        createDialogOpenedRef.current = true;

        try {
            const params = new URLSearchParams(window.location.search);
            params.delete("create");
            const queryString = params.toString();
            // `?create=1` was an INSTRUCTION carried by the landing-page CTA, not a state, so stripping
            // it rewrites an address the member never navigated to.
            replaceInPageHistoryUrl(
                `/groups${queryString ? `?${queryString}` : ""}${window.location.hash}`,
                "consume-one-shot",
            );
        } catch (replaceError) {
            logger.warn("Failed to strip the create param from the groups directory URL", replaceError);
        }

        showCreateGroupDialog(localizer);
    }, [isLoggedIn, localizer]);

    // Debounce the free-text input into the URL-backed query (and clear any active tag).
    React.useEffect(() => {
        const trimmed = inputValue.trim();
        if (trimmed === query) {
            return;
        }
        const timeout = window.setTimeout(() => {
            // First keystroke of a session pushes, the rest carry `continue-editing`, so Back from a
            // search lands on the address from before the search began rather than walking `poetr`,
            // `poet`, `poe`… (§13.4).
            setRouteState(
                (previous) => ({ ...previous, query: trimmed, tag: trimmed ? "" : previous.tag }),
                optionsForTypedValue(trimmed),
            );
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timeout);
    }, [inputValue, optionsForTypedValue, query, setRouteState]);

    // Popular tags feed the chip strip in every mode, so they load once, unconditionally.
    React.useEffect(() => {
        let active = true;
        groupApi.popularGroupTags?.(POPULAR_TAGS_LIMIT)
            .then((response) => { if (active) setPopularTags(response?.data ?? []); })
            .catch((e) => logger.warn("[GroupsDirectoryPage] popular tags failed", e))
            // Settled either way: the reserved strip height must collapse on a failure too, or a page
            // whose tag request 500s keeps a permanent empty band above its results.
            .finally(() => { if (active) setPopularTagsLoaded(true); });
        return () => { active = false; };
    }, [groupApi]);

    /**
     * A search that has ANSWERED and matched nothing. `EMPTY-STATES-94` / `WAVE-14-ANSWERS.md` **Q25**.
     *
     * The same condition that draws `blendSeamIndex === 0` further down, hoisted because the
     * related-groups fetch below needs it too. Three parts, each load-bearing:
     *
     * - `strongMatchCount === 0` — the server said none of these rows matched. `null` means the server
     *   did not say, and must never be read as zero (see `GroupsDirectoryPageResult`).
     * - `!loading` — **this is what keeps it out of a typing session.** `strongMatchCount` is
     *   deliberately NOT cleared between fetches (`EMPTY-STATES-30`), so mid-keystroke it still holds
     *   the previous query's verdict; without this the row would settle and re-settle under the
     *   member's fingers. Q25: "settled once after the search resolves, never during a typing session".
     * - `groups.length > 0` — the blend filled the page, which is the branch this row is about. A
     *   genuinely empty list renders the `EmptyState` instead and has no filler to displace.
     */
    const isSettledZeroMatchSearch = mode === "search"
        && !loading
        && strongMatchCount === 0
        && groups.length > 0;

    /**
     * "Related to groups you're in" is fetched only where it can render — `EMPTY-STATES-55`. It used to
     * be gated on `isLoggedIn` alone, which meant every search and every tag state paid for an
     * authenticated round trip whose response was parsed and then discarded, competing for connections
     * with the search the viewer is actually waiting on.
     *
     * `EMPTY-STATES-94` (**Q25**) adds exactly one more place it can render, and therefore exactly one
     * more place it may be fetched: a **settled zero-match search**. That is not a relaxation of
     * `-55`'s invariant, it is the same invariant re-read — a search that matched nothing is the one
     * search whose response is NOT discarded. A search WITH matches still never fetches this, and no
     * search fetches it while it is in flight.
     *
     * The ref keeps it to one request per page instance: returning to browse from a search must not
     * re-issue a list that has not changed, and neither must a second zero-match query.
     */
    const relatedToMineRequestedRef = React.useRef(false);
    React.useEffect(() => {
        if (!isLoggedIn || relatedToMineRequestedRef.current) {
            return;
        }
        if (mode !== "browse" && !isSettledZeroMatchSearch) {
            return;
        }
        relatedToMineRequestedRef.current = true;
        let active = true;
        paginationApi.groupsRelatedToMine?.()
            .then((response) => { if (active) setRelatedToMine(response?.data ?? []); })
            .catch((e) => logger.warn("[GroupsDirectoryPage] related-to-mine failed", e));
        return () => { active = false; };
    }, [paginationApi, isLoggedIn, mode, isSettledZeroMatchSearch]);

    const fetchPage = React.useCallback(async (cursor?: string): Promise<GroupsDirectoryPageResult> => {
        // All three modes carry the language scope: "mine" (default) narrows to the viewer's language,
        // "all" widens to every language.
        if (mode === "search") {
            // `surface=directory` (BL-169 / FI-168): the backend records every group search as the search
            // tab's `page` surface unless the caller names itself, which pooled this 300 ms type-ahead with
            // the tab's settled queries in the search-calibration readout. The generated call has no
            // `surface` slot, so it rides the request options' params.
            const response = await ClientFactory.getGroupsSearch(query, cursor, languageScope, DIRECTORY_SEARCH_OPTIONS);
            // Search is the only mode that blends: a sparse first page is topped up with tag-bridge and
            // filler rows, and the honest count is the only thing that distinguishes them afterwards.
            return {
                items: response?.data ?? [],
                strongMatches: ClientFactory.readStrongMatchCount(response),
                totalCount: null,
            };
        }
        if (mode === "tag") {
            const response = await ClientFactory.getGroupsByTag(tag, cursor, languageScope);
            return { items: response?.data ?? [], strongMatches: null, totalCount: null };
        }
        // Browse is the public-groups directory: a plain, cursor-paginated catalog of public groups
        // (includes your own groups), not the personalized recommendation rail.
        const response = await ClientFactory.getGroupsDirectory(cursor, languageScope);
        // `DISCOVERY-12` — the scope's total rides the response the page is already waiting on, so the
        // count costs no second request. The server sends it on a first page only; on a load-more the
        // header is absent and the reader returns `null`, which the update below then ignores.
        return {
            items: response?.data ?? [],
            strongMatches: null,
            totalCount: ClientFactory.readTotalCount(response),
        };
    }, [mode, query, tag, languageScope]);

    React.useEffect(() => {
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        // `groups`, `hasMore` and the seam count are deliberately NOT cleared here — `EMPTY-STATES-30`.
        // The fetch effect used to blank them, and the render branched `loading ? skeleton : grid`, so
        // every settled keystroke replaced ~2,800px of results with ~500px of placeholder and expanded
        // it again 300ms later: the comparison the viewer is refining against disappeared, and the
        // height collapse threw away their scroll position. The previous page stays mounted and
        // `aria-busy` until the next one answers; only a true first load paints the skeleton.
        setHasPaginated(false);

        fetchPage()
            .then(({ items, strongMatches, totalCount: total }) => {
                if (requestId !== requestIdRef.current) return;
                setGroups(items);
                setStrongMatchCount(strongMatches);
                setTotalCount(total);
                // All three modes (search, tag, directory browse) are cursor-paginated by the last row.
                setHasMore(items.length > 0 && Boolean(items[items.length - 1]?.paginationParameter));
            })
            .catch((e) => {
                if (requestId !== requestIdRef.current) return;
                setGroups([]);
                setStrongMatchCount(null);
                // Cleared with the list it labels. A total left over from the previous scope would
                // describe a grid that is no longer on screen.
                setTotalCount(null);
                setHasMore(false);
                setError(localizer("groups.directory.error"));
                logger.error("[GroupsDirectoryPage] fetch error", e);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setLoading(false);
            });
    }, [fetchPage, mode, localizer]);

    const handleLoadMore = React.useCallback(() => {
        // `loading` joined this guard with `EMPTY-STATES-30`. The fetch effect used to clear `hasMore`
        // on every refetch, which unmounted this button for the duration; now the previous page stays
        // mounted, so the control stays clickable while a refinement is in flight and would paginate
        // the OLD cursor into a list the refetch is about to replace.
        if (loading || loadingMore || !hasMore) return;
        const cursor = groups[groups.length - 1]?.paginationParameter ?? undefined;
        if (!cursor) {
            setHasMore(false);
            return;
        }
        const requestId = requestIdRef.current;
        setLoadingMore(true);
        fetchPage(cursor)
            // The seam count is deliberately NOT updated here. Only a first page can be blended (the
            // blend nulls every cursor, so a blended page is terminal and has no Load more), which
            // means any page reached through this button is entirely real matches and the seam index
            // established on page 1 still holds.
            .then(({ items }) => {
                if (requestId !== requestIdRef.current) return;
                setGroups((previous) => {
                    const seen = new Set(previous.map((group, index) => getGroupKey(group, index)));
                    const additions = items.filter((group, index) => !seen.has(getGroupKey(group, previous.length + index)));
                    return [...previous, ...additions];
                });
                setHasPaginated(true);
                setHasMore(items.length > 0 && Boolean(items[items.length - 1]?.paginationParameter));
            })
            .catch((e) => {
                if (requestId !== requestIdRef.current) return;
                logger.error("[GroupsDirectoryPage] load more error", e);
            })
            .finally(() => {
                if (requestId === requestIdRef.current) setLoadingMore(false);
            });
    }, [fetchPage, groups, hasMore, loading, loadingMore]);

    // The explicit clear control is a control click, not typing: it pushes (§13.1) and ends the
    // session, so the member's next search opens its own entry.
    const clearSearch = React.useCallback(() => {
        setInputValue("");
        endTypingSession();
        setRouteState((previous) => ({ ...previous, query: "" }));
    }, [endTypingSession, setRouteState]);

    /**
     * Picking a topic PUSHES (§13.1): it changes the address, so Back returns to the previous topic —
     * or, from the first chip, to the unfiltered directory. It also ends any typing session, because
     * the member has moved to another control and the chip clears the box.
     */
    const selectTag = React.useCallback((nextTag: string) => {
        setInputValue("");
        endTypingSession();
        setRouteState((previous) => ({ ...previous, query: "", tag: nextTag }));
    }, [endTypingSession, setRouteState]);

    /**
     * The section heading above the grid, or `null` in browse mode.
     *
     * `DISCOVERY-28`: in browse mode this used to be `groups.directory.discover`, whose value is
     * byte-identical to the page's own `<h1>` in all 25 catalogs — "Discover groups" printed twice,
     * three elements apart. It cost a row of the first screen on a page that already pushes its first
     * card far down on a phone, and it put two identically named headings in the accessibility
     * outline with nothing to tell them apart.
     *
     * The ROW survives, because it also carries the scope control; the duplicate LABEL does not. In
     * browse mode the page has exactly one section, so a second heading naming the same thing was
     * structure without content. In the two narrowing modes the heading says what narrowed the list,
     * which is the thing worth a heading.
     *
     * `groups.directory.discover` itself was deleted from every catalog in the same commit — its only
     * other caller (`HomeFeedList`'s groups-empty-state action) now reads `groups.directory.title`,
     * the identical string.
     */
    const sectionTitle = mode === "search"
        ? localizer("groups.directory.results_for", query)
        : mode === "tag"
            ? localizer("groups.directory.tagged_with", tag)
            : null;

    /**
     * "Groups (142)" beside the scope pill, in browse mode only — `DISCOVERY-12`.
     *
     * `discussion.section.groups_with_count` is reused rather than a new key being minted: its value
     * is generic, it is already translated in all 25 catalogs, and a parenthesised numeral needs no
     * plural branch, which is what the row's own re-pricing named as the expensive half of this fix.
     */
    const directoryTotalLabel = mode === "browse" && totalCount !== null
        ? localizer("discussion.section.groups_with_count", String(totalCount))
        : null;

    const showRelatedRow = mode === "browse" && relatedToMine.length > 0;

    /**
     * The personalised row, in the one branch that has nothing else to offer — `EMPTY-STATES-94`,
     * `WAVE-14-ANSWERS.md` **Q25**.
     *
     * At the moment the product has nothing the member asked for, it is holding the strongest signal
     * it will ever have about them: the groups they are already in. "Four groups adjacent to the ones
     * you belong to" and "six groups that are popular somewhere" are very different consolation
     * prizes, and only the first gives a reason to stay on the page.
     *
     * Same cap as the browse row, so the two states offer the same shape.
     */
    const zeroMatchRelated = isSettledZeroMatchSearch
        // Capped so at least one blend row always survives the displacement below. Q25's constraint is
        // that the never-empty blend stays a feature; a related row that consumed the whole grid would
        // satisfy the letter of "displaces filler" and break the thing the row was told to protect.
        ? relatedToMine.slice(0, Math.max(0, Math.min(ZERO_MATCH_RELATED_LIMIT, groups.length - 1)))
        : [];

    /**
     * The blend rows that survive beside it. Q25: the related row **takes the place of** some blend
     * rows rather than stacking above them, so the scroll cost of the personalised row is zero.
     *
     * Two passes, and they do different jobs: the filter stops the same group appearing twice on one
     * screen (the blend is popular groups, so an overlap is ordinary), and the slice gives back as
     * many rows as the related row spends. The never-empty blend is a defended feature
     * (`2026-07-16-search-recall-repair-and-never-empty-results-design.md`) and none of its cards
     * loses its treatment here — the row is displaced, not demoted.
     */
    const zeroMatchRelatedKeys = new Set(zeroMatchRelated.map((group, index) => getGroupKey(group, index)));
    const zeroMatchFiller = zeroMatchRelated.length > 0
        ? groups
            .filter((group, index) => !zeroMatchRelatedKeys.has(getGroupKey(group, index)))
            .slice(zeroMatchRelated.length)
        : groups;

    const emptyStateTitle = mode === "tag"
        ? localizer("groups.directory.empty_tag.title")
        : mode === "search"
            ? localizer("groups.directory.empty_search.title")
            : localizer("groups.directory.empty.title");

    // What the settled list now holds, for the live region below. In browse mode the only control that
    // can have changed the result set is the language scope, so its own label is the announcement.
    const resultsLabel = sectionTitle
        ?? localizer(languageScope === "mine" ? "profile.language-scope.mine" : "profile.language-scope.all");

    // A true first load has nothing to keep on screen, so it is the only state that paints the
    // skeleton; every later fetch is a refinement of results that stay mounted (`EMPTY-STATES-30`).
    const showSkeleton = loading && groups.length === 0;
    const isRefreshing = loading && groups.length > 0;

    /**
     * `DISCOVERY-25` — one polite region for all three triggers (typing, topic, scope), never one per
     * control, and never attached to the skeleton, which is `aria-hidden` by design.
     *
     * It reports the fetch state and then the outcome: `resultsLabel` when the grid has rows, the
     * mode's empty-state title when it does not. Both are strings the page already owns. It
     * deliberately does NOT report a result count: a count needs plural forms in twenty-four languages
     * for a number that is a page size rather than a total, and the honest total is the backend field
     * `DISCOVERY-14` is still waiting on.
     */
    /**
     * `EMPTY-STATES-52` — the list used to end with its last card, ~70px of background, and nothing:
     * no marker, no "Load more", and no site footer (the template sets `showSiteFooter = false`), so a
     * viewer could not tell the end of the directory from a page that had failed to grow.
     *
     * The blended search page is exactly the state that needs it and exactly the state that cannot use
     * "Load more": the blend nulls every cursor, so `hasMore` is false on a full twenty-card page. The
     * backend half of this row (making the blend cursor-able) is wave 5; this is the marker.
     */
    const showEndOfResults = !loading
        && !loadingMore
        && !error
        && !hasMore
        && groups.length > 0
        && (hasPaginated || groups.length >= END_OF_RESULTS_MIN_ITEMS);

    /**
     * Where the real matches stop and the blended "more you might like" rows start, or `null` for no
     * seam. `EMPTY-STATES-4`.
     *
     * Before 2026-08-16 a zero-match group search was presented as `Results for "…"` above a full grid
     * of groups that plainly did not match, with nothing on screen saying so and the designed
     * `groups.directory.empty_search.*` copy permanently dead — the blend never returns an empty list
     * while any public group exists. The number was computed server-side, recorded for analytics and
     * then dropped, so the client was structurally unable to tell the truth.
     *
     * The fix is to LABEL THE SEAM, never to remove the filler: the never-empty blend is a deliberate,
     * defended feature (`2026-07-16-search-recall-repair-and-never-empty-results-design.md`).
     *
     * Three guards, and each rules out a different way of lying:
     * - search mode only, since nothing else blends;
     * - `null` (server silent) draws nothing, rather than being read as zero;
     * - `>= groups.length` draws nothing, because then every row on screen is a real match and there is
     *   no seam to mark. That also covers the abundant case, where the count is capped at the page size.
     */
    const blendSeamIndex = mode === "search"
        && strongMatchCount !== null
        && strongMatchCount >= 0
        && strongMatchCount < groups.length
        ? strongMatchCount
        : null;

    /**
     * What the page's one live region says, and it is placed AFTER `blendSeamIndex` on purpose.
     *
     * `DISCOVERY-38` (2026-08-23): the zero-match verdict used to be announced by the notice element's
     * own `role="status"`, created inside the results branch together with its text — the construction
     * `SearchResultsPage.tsx:917-925` states in its own comment is not reliably announced. This page
     * already had the reliable one, mounted unconditionally at the top of the results column since
     * `DISCOVERY-25`, so the verdict is routed through THAT rather than through a second live region.
     * One page, one idiom.
     *
     * It also removes a contradiction nobody had noticed: on a zero-match search `groups.length > 0`
     * (the blend fills the page), so this region was announcing `resultsLabel` — "Results for …" —
     * which is exactly the claim the visible notice exists to correct. The announcement now says what
     * the screen says, and `DISCOVERY-43`'s recovery sentence rides along with it.
     */
    const statusMessage = error
        ? error
        : loading
            ? localizer("groups.directory.loading")
            : blendSeamIndex === 0
                ? `${localizer("search.blend_divider.no_matches")} ${localizer("search.no_results.description_everywhere")}`
                : groups.length > 0
                    ? resultsLabel
                    : emptyStateTitle;

    /*
     * `DISCOVERY-59` — a route back to the narrowing controls on the phone.
     *
     * The mobile document measured 8,305 CSS px, 9.9 viewport heights, and every control that narrows
     * 282 groups (the search field, the topic strip, the "Your languages" scope pill) sits above 360
     * CSS px and scrolls away permanently: the rail's sticky behaviour is `lg:` only. "Load more" is at
     * 7,012 px, and loading a second page roughly doubles that. So a visitor 4,000 px in who decides to
     * refine has no route back but ~5 screens of manual scroll, and the same again to return — the
     * inverse of the desktop experience, where the rail follows the reader.
     *
     * The mechanism is `HOME-FEED-29`'s, restated rather than reinvented: the same complaint on the
     * other long list, answered by riding the existing floating-action host instead of adding a second
     * fixed element that would compete with it. A contributed group always wins the corner, so nothing
     * has to coordinate with the registry.
     *
     * Three things this deliberately does NOT do:
     *  - it does not re-declare Create beside the jump the way `HomePage` does. `DISCOVERY-49` gated
     *    Create to desktop on this exact route, and a contributed action bypasses
     *    `isFloatingActionVisible` entirely (the host filters the STATIC registry only), so
     *    re-declaring it here would hand the mobile band straight back to the button the row removed.
     *  - it contributes nothing on desktop, where the rail is already sticky and the row measured no
     *    problem.
     *  - it mints no key: `home.controls.back-to-top` is the same action's word on the other long list
     *    and ships in all 25 catalogs.
     */
    // The host's own definition of "mobile" (`useIsMobileViewport` is the single shared breakpoint), so
    // the contributed jump and the registry's desktop gate can never disagree about which width owns
    // the corner.
    const isMobileViewport = useIsMobileViewport();
    const narrowingControlsRef = React.useRef<HTMLDivElement | null>(null);
    const searchFieldRef = React.useRef<HTMLDivElement | null>(null);
    const [narrowingControlsOutOfView, setNarrowingControlsOutOfView] = React.useState(false);

    React.useEffect(() => {
        const node = narrowingControlsRef.current;
        if (!node || typeof IntersectionObserver === "undefined") {
            return;
        }
        const observer = new IntersectionObserver(
            ([entry]) => setNarrowingControlsOutOfView(!entry.isIntersecting),
            { threshold: 0 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // Scrolls to the SEARCH FIELD, not to the observed row: the observed element is the last of the
    // narrowing controls (the scope pill), and landing on it would leave the field and the topic strip
    // still above the fold — the two controls the row says the visitor came back for.
    const scrollToNarrowingControls = React.useCallback(() => {
        searchFieldRef.current?.scrollIntoView({
            behavior: prefersReducedMotion() ? "auto" : "smooth",
            block: "start",
        });
    }, []);

    useFloatingActionGroup(
        "groups-directory-controls",
        isMobileViewport && narrowingControlsOutOfView
            ? {
                scopeId: "groups-directory-controls",
                actions: [
                    {
                        id: "groups-directory-back-to-controls",
                        look: {
                            icon: ArrowUp,
                            labelKey: "home.controls.back-to-top",
                            variant: "neutral",
                            size: "md",
                        },
                        onClick: scrollToNarrowingControls,
                        dataPlaywrightId: "floating-action-groups-back-to-controls",
                    },
                ],
            }
            : null,
        [isMobileViewport, narrowingControlsOutOfView, scrollToNarrowingControls],
    );

    return (
        <PageWithRailLayout
            dataPlaywrightId="groups-directory-page"
            innerClassName="py-4"
            gridClassName="items-start"
            mainClassName="min-w-0"
            main={(
                <div className="min-w-0">
                    <header className="mb-5">
                        <h1 className="text-2xl font-semibold tracking-tight text-text-main">{localizer("groups.directory.title")}</h1>
                        <p className="mt-1 text-sm leading-6 text-neutral-text">{localizer("groups.directory.subtitle")}</p>
                    </header>

                    <div className="relative mb-4" ref={searchFieldRef}>
                        <Search className="pointer-events-none absolute left-3 top-1/2 size-4.5 -translate-y-1/2 text-neutral-text" aria-hidden={true} />
                        <input
                            type="search"
                            value={inputValue}
                            onChange={(event) => setInputValue(event.target.value)}
                            placeholder={localizer("groups.directory.search_placeholder")}
                            aria-label={localizer("groups.directory.search_placeholder")}
                            className={cn(
                                // `[@media(pointer:coarse)]:text-base` is the iOS zoom floor, not styling:
                                // Safari auto-zooms the page by exactly 16/font-size when a control under
                                // 16px takes focus. Found at 14px by the real-device field audit,
                                // `make ios-field-zoom-audit`.
                                "w-full rounded-full border border-border bg-card py-3 pl-10 pr-10 text-sm [@media(pointer:coarse)]:text-base text-text-main",
                                "placeholder:text-neutral-text outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                            )}
                            data-playwright-id="groups-directory-search-input"
                        />
                        {inputValue ? (
                            <button
                                type="button"
                                onClick={clearSearch}
                                aria-label={localizer("groups.directory.clear_search")}
                                // `touch-hit-44` — `DISCOVERY-67`. This glyph renders at 28 CSS px, and
                                // it is the one-tap way out of exactly the state the row was captured
                                // in: the page's own advice is "try different words" and a mis-tap puts
                                // the caret in a field that still holds the failed query. The EXPANDER
                                // rather than a grown box because the box must not change — the button
                                // is anchored at `right-3` inside the field's `pr-10`, so growing it to
                                // 44 px would drag the glyph inward over the query text.
                                //
                                // The pitch rule is satisfied by construction here: the only control
                                // whose slug this one can overlap is the input it sits in, and both go
                                // to the same place as far as a thumb is concerned — the button is
                                // painted later, so in the overlap band clearing wins, which is the
                                // outcome the row asks for. `absolute` is the containing block the
                                // utility needs (check B of `check-tap-target-mechanisms.mjs`).
                                className="touch-hit-44 absolute right-3 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-neutral-text transition-colors hover:bg-muted hover:text-text-main"
                                data-playwright-id="groups-directory-search-clear"
                            >
                                <X className="size-4" aria-hidden={true} />
                            </button>
                        ) : null}
                    </div>

                    {/*
                      * Topic chips — the mobile mirror of the rail's topic list. The rail paints its
                      * own copy only from `lg` up and this one only below `lg`, so exactly one is
                      * visible at any viewport.
                      *
                      * Three things changed here on 2026-08-17:
                      * - it no longer stops rendering in search mode (`EMPTY-STATES-33`). It was gated
                      *   `mode !== "search"`, so on a phone a query hid the topic chips at the same
                      *   moment the rail hid the create card, leaving a search box, a heading and an
                      *   unbroken column of cards with no secondary action anywhere. A query is when
                      *   alternative topics are MOST useful, not least;
                      * - one scrollable row instead of a wrapping cloud. The wrapped block measured
                      *   ~254 CSS px of a 915 px phone viewport, so the first screen of a discovery
                      *   page showed about a third of one card (`DISCOVERY-13`);
                      * - the row's height is RESERVED while the tags are still in flight, with
                      *   `aria-hidden` placeholder chips at the same height. `popularTags` starts empty
                      *   and arrives from a client fetch, so the block used to appear after first paint
                      *   and shove the whole list down — exactly where a thumb was about to tap.
                      */}
                    {!popularTagsLoaded || popularTags.length > 0 ? (
                        <div
                            className="mb-5 lg:hidden"
                            data-playwright-id="groups-directory-topic-strip"
                        >
                            {/* `EMPTY-STATES-92` / `DISCOVERY-45` — the name the desktop rail already
                                owns, now WRITTEN here as well as announced. At >=1024px this identical
                                list sits in a bordered panel headed `groups.directory.topics.title`;
                                on a phone that panel is gone and what remained was a bare scroller of
                                chips directly under the search field, which is where a filter chip
                                lives. `DISCOVERY-24` gave each chip `aria-pressed`, so the individual
                                chips announce their state; the group of them announced nothing, and
                                nothing on screen said what the group was.

                                **The visible caption is the destructive half's answer** (`Q25`, wave
                                14): tapping one of these chips does not narrow the query, it REPLACES
                                it — `selectTag` clears the box and pushes a tag route — and until now
                                a member discovered that afterwards, with their typed words gone and
                                nothing indicating anything had been thrown away. Q25 chose to say it
                                "before or as it happens", for no copy. "Browse by topic" over a row of
                                pills says, before the tap, that this browses rather than filters; the
                                same row unlabelled says the opposite by its position.

                                No new key either way: `groups.directory.topics.title` ships in all 25
                                catalogs. The scroller keeps `role="group"` and now takes its name FROM
                                the visible caption, so the written word and the announced one cannot
                                drift apart. */}
                            <div
                                id={topicsCaptionId}
                                className="mb-2 flex items-center gap-2 text-sm font-semibold text-text-main"
                            >
                                {/* `text-neutral` on the ornament, matching the rail: an `aria-hidden`
                                    glyph beside its own written label conveys nothing the label does
                                    not, which is the fill role that token exists for. */}
                                <Hash className="size-4 text-neutral" aria-hidden={true} />
                                {localizer("groups.directory.topics.title")}
                            </div>
                            {/* `DISCOVERY-55` — the fade overlays are positioned against THIS box, so it
                                is `relative` and adds no height of its own; the scroller inside keeps
                                the reserved row height `DISCOVERY-13` established. */}
                            <div className="relative" data-playwright-id="groups-directory-topic-scroller">
                            <div
                                ref={topicScrollerRef}
                                onScroll={syncTopicScrollerClippedEdges}
                                // `[@media(pointer:coarse)]:h-11` — `DISCOVERY-67`, and it is the ROW
                                // half of that fix rather than styling. `overflow-x: auto` computes
                                // `overflow-y` to a clipping value, so a chip grown to the 44 px floor
                                // (or a centred `touch-hit-44` slug, which is why the expander is not
                                // the tool here) is clipped by this container unless the container grows
                                // with it. The strip is 36 px on a fine pointer, exactly as before.
                                className="flex h-9 [@media(pointer:coarse)]:h-11 items-center gap-2 overflow-x-auto overscroll-x-contain"
                                role="group"
                                aria-labelledby={topicsCaptionId}
                            >
                                {popularTagsLoaded ? (
                                    <>
                                        {tag ? (
                                            <TagChip
                                                label={localizer("groups.directory.topics.all")}
                                                onClick={() => selectTag("")}
                                                className="shrink-0"
                                            />
                                        ) : null}
                                        {popularTags.slice(0, MOBILE_TOPIC_CHIPS_LIMIT).map((popularTag) => (
                                            <TagChip
                                                key={popularTag.name ?? ""}
                                                label={popularTag.name ?? ""}
                                                count={popularTag.groupCount}
                                                active={Boolean(popularTag.name) && popularTag.name === tag}
                                                onClick={() => selectTag(popularTag.name ?? "")}
                                                className="shrink-0"
                                            />
                                        ))}
                                    </>
                                ) : (
                                    Array.from({ length: 5 }).map((_, index) => (
                                        <div
                                            key={index}
                                            aria-hidden={true}
                                            className="h-7 w-24 shrink-0 animate-pulse rounded-full border border-border bg-muted"
                                        />
                                    ))
                                )}
                            </div>
                            {/* The overflow affordance, the same one `SearchTabsNav` ships. `aria-hidden`
                                and `pointer-events-none`: a visual cue that the row continues, not
                                content and not a control — every chip is already in the tab order. Each
                                side renders only while THAT side is clipped, so a strip that fits shows
                                nothing at all. */}
                            {topicScrollerClippedEdges.start ? (
                                <span
                                    aria-hidden="true"
                                    data-playwright-id="groups-directory-topics-fade-start"
                                    className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-background to-transparent"
                                />
                            ) : null}
                            {topicScrollerClippedEdges.end ? (
                                <span
                                    aria-hidden="true"
                                    data-playwright-id="groups-directory-topics-fade-end"
                                    className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-background to-transparent"
                                />
                            ) : null}
                            </div>
                        </div>
                    ) : null}

                    {showRelatedRow ? (
                        <section className="mb-6" data-playwright-id="groups-directory-related-to-mine">
                            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-main">
                                <Sparkles className="size-4 text-neutral" aria-hidden={true} />
                                {localizer("groups.directory.related_to_mine")}
                            </div>
                            <GroupsGrid groups={relatedToMine.slice(0, 4)} localizer={localizer} />
                        </section>
                    ) : null}

                    {/*
                      * `DISCOVERY-12`, the half the 2026-08-17 legibility fix left open.
                      *
                      * The directory arrives narrowed to the viewer's reading languages, and the scope
                      * pill announcing that is a claim about the CONTROL. The count is a claim about
                      * the RESULT, which is the one a visitor is actually reading the page for:
                      * widening the scope now visibly changes a number, so the filter's effect is
                      * observable and not merely its state.
                      *
                      * Browse only. Search has its own honest figure (`strongMatchCount`, the blend
                      * seam) and the tag hub carries its subject in the heading; a third numeral in
                      * the same row would compete with both.
                      *
                      * `null` renders nothing at all — a continuation page, an older backend or a
                      * proxy that dropped the header. It is never coerced to zero.
                      *
                      * A `<span>` rather than a heading, deliberately: `DISCOVERY-28` deleted an `<h2>`
                      * from this exact row because it repeated the page's `<h1>` in the accessibility
                      * outline. A count is content, not a second landmark.
                      */}
                    {/* `DISCOVERY-59` — the LAST of the narrowing controls, and therefore what decides
                        whether the jump back to them is owed. Observing this row rather than the search
                        field means the jump appears only once nothing that narrows 282 groups is on
                        screen any more, not merely once the field has gone. */}
                    <div
                        ref={narrowingControlsRef}
                        className={cn(
                            "mb-3 flex items-center gap-3",
                            sectionTitle || directoryTotalLabel ? "justify-between" : "justify-end",
                        )}
                    >
                        {sectionTitle ? (
                            <h2 className="min-w-0 truncate text-sm font-semibold text-text-main">{sectionTitle}</h2>
                        ) : directoryTotalLabel ? (
                            <span
                                className="min-w-0 truncate text-sm font-medium text-neutral-text"
                                data-playwright-id="groups-directory-total-count"
                            >
                                {directoryTotalLabel}
                            </span>
                        ) : null}
                        <GroupLanguageScopeSelect value={languageScope} onChange={setLanguageScope} localizer={localizer} />
                    </div>

                    {/*
                      * `DISCOVERY-25` — the page's one live region, for all three refetch triggers.
                      *
                      * Mounted unconditionally and never keyed: a region that appears at the moment its
                      * text changes is a region assistive technology has not been watching, so nothing
                      * is announced. The skeleton stays `aria-hidden` — it is decoration for the eye,
                      * and this is the channel for everyone else.
                      */}
                    <div
                        role="status"
                        aria-live="polite"
                        className="sr-only"
                        data-playwright-id="groups-directory-status"
                    >
                        {statusMessage}
                    </div>

                    {error ? <WarningBanner description={error} className="mb-4 py-3" /> : null}
                    {/*
                      * groups_directory.jte server-renders crawlableGroups as real anchors inside this
                      * island, and the catch above sets groups to [] — so before 2026-08-04 a failed
                      * fetch replaced the directory a crawler had been given with a warning banner and
                      * an empty state (frontend F2). Restores only on the error branch; a genuinely
                      * empty directory keeps its empty state.
                      */}
                    {error ? (
                        <SsrRestoredList
                            componentName="GroupsDirectoryPage"
                            selector={CRAWLABLE_GROUP_LIST_SSR_SELECTOR}
                        />
                    ) : null}

                    {/*
                      * `EMPTY-STATES-30` — the results container, not a `loading ? skeleton : grid`
                      * switch. A refinement keeps the previous page mounted and dimmed, so neither the
                      * document height nor the scroll position moves while the next one is in flight;
                      * only a true first load has nothing to keep and paints the skeleton.
                      */}
                    <div
                        aria-busy={loading || loadingMore}
                        data-playwright-id="groups-directory-results"
                        className={cn(isRefreshing && "opacity-60 transition-opacity duration-150")}
                    >
                    {showSkeleton ? (
                        <GroupsGridSkeleton />
                    ) : groups.length > 0 ? (
                        <>
                            {blendSeamIndex === null ? (
                                <GroupsGrid groups={groups} localizer={localizer} />
                            ) : blendSeamIndex === 0 ? (
                                // Every row is filler. The notice goes ABOVE the grid, which is what
                                // "none of these are matches" looks like.
                                //
                                // `EMPTY-STATES-50` — and the create offer goes with it. Someone who
                                // types a community name here and matches nothing has told the product,
                                // in their own words, exactly what they want that does not exist, and
                                // unlike a discussion topic, creating it is a single dialog the product
                                // already has. Until now that moment produced twenty unrelated groups
                                // and no create affordance in the main column at all: the rail card is
                                // off to the side on desktop and, below `lg`, paints AFTER the filler
                                // grid, which is `EMPTY-STATES-33` restated. `requiresLogin` plus the
                                // shared account-access surface so an anonymous visitor meets the
                                // dialog rather than a dead button.
                                //
                                // Wave 8 (2026-08-22) closes the remainder: the SEED.
                                // `showUpdateGroupSettingsDialog` already took a `name` and treats a
                                // call without a `groupPublicId` as a creation, so carrying the search
                                // string into the form was an argument, not a new capability — which
                                // is what the wave-6 pass could not find. A specific intent no longer
                                // arrives at a blank form.
                                //
                                // The label stays plain, and that is now a decision rather than a
                                // deferral. The seed IS the query named back: the member sees their
                                // own words already filled into the name field, in the place they
                                // would otherwise have to retype them, which says "we heard you" more
                                // convincingly than a button caption and does not spend a key with an
                                // interpolated user string across 25 locales to say it.
                                <div className="space-y-4">
                                    <div className="space-y-3" data-playwright-id="groups-directory-zero-match-offer">
                                        {/* `allVerticalsEmpty` is passed EXPLICITLY, and true is the
                                            only defensible value here. `DISCOVERY-51` gave the divider
                                            two sentences and let the page choose: `description` says
                                            "check the other tabs", `_everywhere` denies the whole site.
                                            This route has no tab strip, so the first sentence names a
                                            control that does not exist on it — and it is what an
                                            unpassed prop silently selects. The prior verdict here was
                                            `_everywhere` (`DISCOVERY-43`, pinned by this page's spec),
                                            so this keeps the shipped copy rather than changing it in an
                                            integration.
                                            Whether `_everywhere` is the RIGHT sentence for a failed
                                            group search is a live product question, not this fix:
                                            wave 19 Q1 owns it (`DISCOVERY-73`, `DISCOVERY-86`). */}
                                        <SearchBlendDivider localizer={localizer} strongMatchCount={0} allVerticalsEmpty />
                                        <div className="flex flex-wrap items-center justify-center gap-3">
                                            <CtaButton
                                                icon={PlusCircle}
                                                text={localizer("groups.directory.create.button")}
                                                displayTextOnMobile={true}
                                                variant="primary"
                                                requiresLogin
                                                accountAccessDialog={CREATE_GROUP_ACCOUNT_ACCESS_DIALOG}
                                                playwrightId="groups-directory-zero-match-create"
                                                onClick={() => { showUpdateGroupSettingsDialog({ localizer, name: query }); }}
                                            />
                                            {/*
                                              * `EMPTY-STATES-93` — a second move, and a
                                              * NON-COMMITTAL one.
                                              *
                                              * `getGroupsSearch(query, cursor, languageScope)` is
                                              * scoped and the scope defaults to "mine", so "Nothing
                                              * matched your search" is true only inside a narrowing
                                              * the member never applied — and the only thing the
                                              * state offered them was to found a community. That is
                                              * a commitment (you now lead something you must
                                              * populate), so a member who does not want it had
                                              * nothing to click at all; and the concrete failure is
                                              * worse than a dead end, because a group whose name is
                                              * in another language exists, is hidden by the scope,
                                              * and the product hands the member a pre-filled form to
                                              * create a second copy of it.
                                              *
                                              * Widening is the honest second action precisely
                                              * because it is the filter that produced the claim.
                                              * `profile.language-scope.all` is the very string the
                                              * scope control beside it already uses, in all 25
                                              * catalogs, so this costs NO key — which is what the
                                              * row predicted.
                                              *
                                              * Gated on the narrowing being applied: at "all" there
                                              * is nothing left to widen, and a button that widens
                                              * nothing would be a new false claim in the place a
                                              * false claim was just removed.
                                              */}
                                            {languageScope === "mine" ? (
                                                <CtaButton
                                                    icon={Languages}
                                                    text={localizer("profile.language-scope.all")}
                                                    displayTextOnMobile={true}
                                                    variant="outline"
                                                    playwrightId="groups-directory-zero-match-widen-languages"
                                                    onClick={() => { setLanguageScope("all"); }}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                    {/*
                                      * `EMPTY-STATES-94` (**Q25**) — the personalised row, in the one
                                      * branch that has nothing else to offer.
                                      *
                                      * Placed BELOW the verdict on purpose. The section heading above
                                      * this whole block still reads `Results for "…"`, and the
                                      * `SearchBlendDivider` at zero has already said, in its own
                                      * words, that nothing matched — so by the time the reader
                                      * arrives here the header has been corrected and this row makes
                                      * no claim it could contradict. It also carries its own
                                      * translated heading, which is why Q25 costs no key: the row
                                      * says what it is.
                                      *
                                      * Same markup as the browse-mode row above, deliberately — one
                                      * idiom, one hook, so a member meets the same thing twice rather
                                      * than two things that look alike.
                                      */}
                                    {zeroMatchRelated.length > 0 ? (
                                        <section data-playwright-id="groups-directory-related-to-mine">
                                            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-text-main">
                                                <Sparkles className="size-4 text-neutral" aria-hidden={true} />
                                                {localizer("groups.directory.related_to_mine")}
                                            </div>
                                            <GroupsGrid
                                                groups={zeroMatchRelated}
                                                localizer={localizer}
                                                playwrightId="groups-directory-zero-match-related-grid"
                                            />
                                        </section>
                                    ) : null}
                                    <GroupsGrid groups={zeroMatchFiller} localizer={localizer} />
                                </div>
                            ) : (
                                // Some matched. Split the grid at the seam, exactly as /search does.
                                <div className="space-y-4">
                                    <GroupsGrid groups={groups.slice(0, blendSeamIndex)} localizer={localizer} />
                                    <SearchBlendDivider localizer={localizer} strongMatchCount={blendSeamIndex} />
                                    <GroupsGrid
                                        groups={groups.slice(blendSeamIndex)}
                                        localizer={localizer}
                                        playwrightId="groups-directory-filler-grid"
                                    />
                                </div>
                            )}
                            {hasMore ? (
                                <div className="mt-6 flex justify-center">
                                    <CtaButton
                                        icon={ChevronDown}
                                        text={localizer(loadingMore ? "groups.directory.loading" : "groups.directory.load_more")}
                                        displayTextOnMobile={true}
                                        variant="outline"
                                        disabled={loading || loadingMore}
                                        isLoading={loadingMore}
                                        playwrightId="groups-directory-load-more"
                                        onClick={handleLoadMore}
                                    />
                                </div>
                            ) : null}
                            {showEndOfResults ? (
                                <div className="mt-2" data-playwright-id="groups-directory-end-of-results">
                                    <PaginatedListStatusText>{localizer("home.end-of-list")}</PaginatedListStatusText>
                                </div>
                            ) : null}
                        </>
                    ) : mode === "tag" ? (
                        <EmptyState
                            title={localizer("groups.directory.empty_tag.title")}
                            description={localizer("groups.directory.empty_tag.description", tag)}
                            action={(
                                <CtaButton icon={X} text={localizer("groups.directory.topics.all")} displayTextOnMobile={true} variant="outline" onClick={() => selectTag("")} />
                            )}
                        />
                    ) : mode === "search" ? (
                        <EmptyState
                            title={localizer("groups.directory.empty_search.title")}
                            description={localizer("groups.directory.empty_search.description", query)}
                            action={(
                                <CtaButton icon={X} text={localizer("groups.directory.empty_search.clear")} displayTextOnMobile={true} variant="outline" onClick={clearSearch} />
                            )}
                        />
                    ) : (
                        <EmptyState
                            title={localizer("groups.directory.empty.title")}
                            description={localizer("groups.directory.empty.description")}
                            action={(
                                <CtaButton
                                    icon={PlusCircle}
                                    text={localizer("groups.directory.create.button")}
                                    displayTextOnMobile={true}
                                    variant="primary"
                                    requiresLogin
                                    accountAccessDialog={CREATE_GROUP_ACCOUNT_ACCESS_DIALOG}
                                    onClick={() => { showCreateGroupDialog(localizer); }}
                                />
                            )}
                        />
                    )}
                    </div>
                </div>
            )}
            // `DISCOVERY-11` / `EMPTY-STATES-33`: the rail carries the ONLY create-a-group control on
            // this page, so dropping it below `lg` left a phone with no way to create a group at all.
            // It now renders at every breakpoint; below `lg` the grid is one column, so it paints after
            // the results, which is where "none of these is my thing" actually happens.
            railOnMobile
            rail={(
                <DirectoryRail
                    localizer={localizer}
                    popularTags={popularTags}
                    activeTag={tag}
                    onSelectTag={selectTag}
                    // Only in search mode: a query is the member's own words for the community they
                    // want. In browse or tag mode there is nothing they typed, so the form stays blank.
                    seedName={mode === "search" ? query : ""}
                />
            )}
        />
    );
}

export const mountGroupsDirectoryPage = createReactMount<GroupsDirectoryPageProps>({
    name: "mountGroupsDirectoryPage",
    getProps: ({ dataset, localizer }) => ({
        initialQuery: decodeURIComponent(dataset.query ?? ""),
        initialTag: decodeURIComponent(dataset.tag ?? ""),
        localizer,
    }),
    render: (props) => <GroupsDirectoryPage {...props} />,
});
